import BigNumber from 'bignumber.js';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import _isObject from 'lodash/isObject';
import { Money } from '~/types/money';
import { isDefined } from '~/types/typeGuards';

const moneyFormatters: Record<string, Intl.NumberFormat | undefined> = {};

const bigNumberToString = (value: BigNumber): string => {
  return value.toString();
};

export function formatMoney(money: Money): string {
  if (!moneyFormatters[money.currency]) {
    moneyFormatters[money.currency] = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: money.currency,
      useGrouping: 'always',
    });
  }
  const formatter = moneyFormatters[money.currency] as Intl.NumberFormat;
  const amount = BigNumber(money.amount);
  // Handle -0 case: if the value is zero, ensure it's positive zero
  const valueToFormat = amount.isZero() ? BigNumber(0) : amount;
  return formatter.format(valueToFormat.toNumber());
}

export function formatMoneyAmount(money: Money): string {
  if (!moneyFormatters[money.currency]) {
    moneyFormatters[money.currency] = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: money.currency,
      useGrouping: 'always',
    });
  }
  const formatter = moneyFormatters[money.currency] as Intl.NumberFormat;
  const amount = BigNumber(money.amount);
  const valueToFormat = amount.isZero() ? BigNumber(0) : amount;
  return formatter
    .formatToParts(valueToFormat.toNumber())
    .filter((p) => p.type !== 'currency' && p.type !== 'literal')
    .map((p) => p.value)
    .join('');
}

export const formatMoneyWithSign = (money: Money) => {
  const formatter = new Intl.NumberFormat('it-IT', {
    currency: money.currency,
    signDisplay: 'exceptZero',
    style: 'currency',
    useGrouping: 'always',
  });

  const amount = BigNumber(money.amount);
  return formatter.format(amount.toNumber());
};

export const formatSign = (currency: Money['currency']) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
    .formatToParts(1)
    .find((x) => x.type === 'currency')?.value;
};

export const formatMoneyNoDecimals = (money: Money) => {
  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    useGrouping: 'always',
  });

  const amount = BigNumber(money.amount);
  return formatter.format(amount.toNumber());
};

export function formatMoneyOptions(
  money: Money,
  options?: { maximumFractionDigits?: number; minimumFractionDigits?: number },
): string {
  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: options?.maximumFractionDigits,
    minimumFractionDigits: options?.minimumFractionDigits,
    useGrouping: 'always',
  });

  const amount = BigNumber(money.amount);
  return formatter.format(amount.toNumber());
}

export const formatMoneyShort = (money: Money): string => {
  const currency = money.currency;
  const amount = BigNumber(money.amount).toNumber();
  const absoluteValue = Math.abs(amount);
  if (absoluteValue < 10_000_000) {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(amount);
  }

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
    notation: 'compact',
    compactDisplay: 'short',
  })
    .format(amount)
    .replace('Mio\xA0', 'M')
    .replace('Mln\xA0', 'M');
};

export function sumMoney(...args: Money[]): Money {
  if (_isEmpty(args)) {
    throw new TypeError('Sum with empty operands');
  } else if (args.length == 1) {
    return args[0]!;
  }

  if (hasMixedCurrencies(...args)) {
    throw new TypeError('Operands have different curencies');
  }

  const currency = args[0]!.currency;
  const totalAmount = args.map((v) => BigNumber(v.amount)).reduce((acc, val) => acc.plus(val), BigNumber('0'));
  return { currency, amount: bigNumberToString(totalAmount) };
}

export function maybeSumMoney(leftMoney?: Money | null, rightMoney?: Money | null): Money | null {
  const isLeftValid = isDefined(leftMoney) && BigNumber(leftMoney.amount).isFinite();
  const isRightValid = isDefined(rightMoney) && BigNumber(rightMoney.amount).isFinite();

  if (isLeftValid && isRightValid) {
    if (hasMixedCurrencies(leftMoney, rightMoney)) {
      return null;
    }

    return sumMoney(leftMoney, rightMoney);
  }

  if (isLeftValid) return leftMoney;
  if (isRightValid) return rightMoney;

  return null;
}

export function subtractMoney(leftMoney: Money, rightMoney: Money): Money {
  if (leftMoney.currency !== rightMoney.currency) {
    throw new TypeError('Operands have different currencies');
  }

  const leftAmount = BigNumber(leftMoney.amount);
  const rightAmount = BigNumber(rightMoney.amount);
  const resultAmount = leftAmount.minus(rightAmount);

  return {
    currency: leftMoney.currency,
    amount: resultAmount.toString(),
  };
}

export function negateMoney(money: Money): Money {
  return {
    currency: money.currency,
    amount: bigNumberToString(BigNumber(money.amount).negated()),
  };
}

export function maybeNegateMoney(money?: Money | null): Money | null {
  if (!isDefined(money)) return null;

  return negateMoney(money);
}

export function absMoney(money: Money): Money {
  return {
    currency: money.currency,
    amount: bigNumberToString(BigNumber(money.amount).abs()),
  };
}

/**
 * Compares two Money values.
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b
 * Throws if currencies are different.
 */
export function compareMoney(a: Money, b: Money): number {
  if (a.currency !== b.currency) {
    throw new TypeError('Cannot compare money with different currencies');
  }

  const result = BigNumber(a.amount).comparedTo(BigNumber(b.amount));
  return result ?? 0;
}

/**
 * Compares two Money values by absolute value (magnitude).
 * Returns: -1 if |a| < |b|, 0 if |a| === |b|, 1 if |a| > |b|
 * Throws if currencies are different.
 */
export function compareMoneyAbs(a: Money, b: Money): number {
  if (a.currency !== b.currency) {
    throw new TypeError('Cannot compare money with different currencies');
  }

  const result = BigNumber(a.amount).abs().comparedTo(BigNumber(b.amount).abs());
  return result ?? 0;
}

export function isNotZero(money?: Money | null): boolean {
  if (!isDefined(money)) {
    return false;
  }

  return !BigNumber(money.amount).isZero();
}

export function isZero(money?: Money | null): boolean {
  if (!isDefined(money)) {
    return false;
  }

  return BigNumber(money.amount).isZero();
}

export function isMoreThanZero(money: Money): boolean {
  return BigNumber(money.amount).gt(0);
}

export function isLessThanZero(money: Money): boolean {
  return BigNumber(money.amount).lt(0);
}

export function maxMoney(...args: Money[]): Money | undefined {
  if (args.length === 0) return undefined;
  const currency = args[0]!.currency;

  const arr = args.map((money) => {
    if (money.currency !== currency) {
      throw new TypeError('Operands have different curencies');
    }

    return BigNumber(money.amount);
  });

  const maxValue = BigNumber.max.apply(null, arr);
  return {
    amount: bigNumberToString(maxValue),
    currency,
  };
}

export function minMoney(...args: Money[]): Money | undefined {
  if (args.length === 0) return undefined;
  const currency = args[0]!.currency;

  const arr = args.map((money) => {
    if (money.currency !== currency) {
      throw new TypeError('Operands have different curencies');
    }

    return BigNumber(money.amount);
  });

  const maxValue = BigNumber.min.apply(null, arr);
  return {
    amount: bigNumberToString(maxValue),
    currency,
  };
}

export function isMoneyGreaterThan(moneyA: Money, moneyB: Money) {
  if (moneyA.currency !== moneyB.currency) {
    throw new TypeError('Operands have different curencies');
  }
  return BigNumber(moneyA.amount).isGreaterThan(moneyB.amount);
}

export function isMoneyLessThan(moneyA: Money, moneyB: Money) {
  if (moneyA.currency !== moneyB.currency) {
    throw new TypeError('Operands have different curencies');
  }
  return BigNumber(moneyA.amount).isLessThan(moneyB.amount);
}

export const ensureDecimalPlaces = (money: Money, decimalPlaces: number) => {
  return {
    currency: money.currency,
    amount: BigNumber(money.amount).toFixed(decimalPlaces),
  };
};

export function newMoney(amount: string | number, currency: Money['currency'] = 'EUR'): Money {
  return {
    amount: bigNumberToString(BigNumber(amount)),
    currency,
  };
}

export function getNumberAmount(money: Money): number {
  return BigNumber(money.amount).toNumber();
}

export function maybeGetNumberAmount(money?: Money | null): number | null {
  if (!isDefined(money)) return null;
  return getNumberAmount(money);
}

export function hasMixedCurrencies(...args: Money[]): boolean {
  const currencies = [...new Set(args.map((el) => el.currency))];
  return currencies.length > 1;
}

export function multiplyMoney(money: Money, multiplier: number): Money {
  return {
    currency: money.currency,
    amount: bigNumberToString(BigNumber(money.amount).multipliedBy(multiplier)),
  };
}

export const isMoney = (money: unknown): money is Money => {
  if (_isNil(money) || !isObject(money)) return false;
  if (Object.keys(money).length === 2 && 'amount' in money && 'currency' in money) {
    if (!_isNil(money.amount) && !_isNil(money.currency)) {
      return true;
    }
  }
  return false;
};
